import isPropValid from "@emotion/is-prop-valid";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import FlagProvider from "@unleash/proxy-client-react";
import { DatadogProvider } from "Components/Datadog/DatadogProvider";
import { FMSTextLoader } from "Components/FMSTextLoader";
import { TextProvider } from "Components/TextContext";
import dittoData from "DittoData/index";
import store from "Store/index";
import { getUnleashConfig } from "Utils/unleash";
import { DittoProvider } from "ditto-react";
import React, { ComponentProps } from "react";
import { Provider as ReduxProvider } from "react-redux";
import { StyleSheetManager } from "styled-components";
import { CustomThemeProvider } from "./CustomThemeProvider";
import { UnleashContextWrapper } from "./UnleashContextWrapper";

const DEFAULT_CLIENT = new QueryClient();

type AppProviderProps = ComponentProps<typeof FMSTextLoader> &
  ComponentProps<typeof CustomThemeProvider>;

export const AppProvider = ({
  documentId,
  brandingType,
  children,
}: React.PropsWithChildren<AppProviderProps>): React.ReactElement => (
  <ReduxProvider store={store}>
    <QueryClientProvider client={DEFAULT_CLIENT}>
      <FlagProvider config={getUnleashConfig()}>
        <UnleashContextWrapper>
          <StyleSheetManager shouldForwardProp={shouldForwardProp}>
            <CustomThemeProvider brandingType={brandingType}>
              <FMSTextLoader documentId={documentId}>
                <DittoProvider source={dittoData} variant="base">
                  <TextProvider>
                    <DatadogProvider>{children}</DatadogProvider>
                  </TextProvider>
                </DittoProvider>
              </FMSTextLoader>
            </CustomThemeProvider>
          </StyleSheetManager>
        </UnleashContextWrapper>
      </FlagProvider>
    </QueryClientProvider>
  </ReduxProvider>
);

// This implements the default behavior from styled-components v5
function shouldForwardProp(propName: string, target: unknown): boolean {
  if (typeof target === "string") {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
}
